import { FormGroup, FormGroupFeedback, ISelectOption, Intent } from '@pinpointhq/thumbtack';
import * as React from 'react';
import Select from 'react-select';
import IReactSelectOption from '../../../interfaces/IReactSelectOption';
import { useCustomEventEffect } from '../../hooks';
import { sanitizeContent } from '../../thumbtack';
import toSentence from '../../utilities/toSentence';
import { HiddenInputs } from './shared/HiddenInputs';
import {
  IAnswerMatchParams,
  IQuestionAnswerUpdateEventDetails,
  IQuestionDetails,
  matchQuestionConditions,
} from './shared/utilities';

interface ISingleSelect {
  multipleChoiceHelperText: string;
  questionDetails: IQuestionDetails;
  attributeName?: string;
  inputId?: string;
  initialErrors?: string[];
  name: string;
  options: { value: number | string; label: string }[];
  placeholder?: string;
  storedOption?: number | string;
  useMobileSelect?: boolean;
  conditionalQuestionParentQuestionId: string;
  conditionalQuestionConditions: { operator: string; value: string }[];
  formResourceType: 'application' | 'job_seeker';
}

export default function SingleSelect(props: ISingleSelect) {
  const {
    multipleChoiceHelperText,
    questionDetails,
    attributeName,
    inputId,
    initialErrors = [],
    name,
    options,
    placeholder = 'Select...',
    storedOption,
    useMobileSelect = false,
    conditionalQuestionConditions,
    conditionalQuestionParentQuestionId,
    formResourceType,
  } = props;
  const { index, questionId, questionType, title, additionalInformation, required } = questionDetails;
  const condition = conditionalQuestionConditions && conditionalQuestionConditions[0];
  const [visible, setVisible] = React.useState<boolean>(conditionalQuestionParentQuestionId === null);
  const [errors, setErrors] = React.useState<string[]>(initialErrors);
  const [selectedOption, setSelectedOption] = React.useState<typeof options[number]>();

  /**
   * Sender - announce to possible conditional question answers when the value of this rendered component changes
   */
  const { dispatch: announceAnswerUpdate } = useCustomEventEffect(`question:${questionId}:answer:update`);

  React.useEffect(() => {
    setTimeout(() => {
      announceAnswerUpdate({ questionType: 'multiple_choice', maxNumAnswerOptions: 1, value: selectedOption?.value });
    }, 250);
  }, []);

  /**
   * Receiver - receive announcements from possible parent conditional questions about their changing value(s) so
   * we know whether to hide or show this answer component
   */
  useCustomEventEffect(
    `question:${conditionalQuestionParentQuestionId}:answer:update`,
    ({ detail }: CustomEvent<IQuestionAnswerUpdateEventDetails>) => {
      const matchParams: IAnswerMatchParams = {
        questionType: detail.questionType,
        value: detail.value,
        parentQuestionCondition: condition,
        maxNumAnswerOptions: detail.maxNumAnswerOptions,
      };
      // Only hide element if it's got a parent conditional question
      if (conditionalQuestionParentQuestionId) {
        setVisible(matchQuestionConditions(matchParams));
      }
    },
  );

  React.useEffect(() => {
    if (storedOption) {
      const option = options.find((selectOption: ISelectOption) => selectOption.value === storedOption);
      setSelectedOption(option);
      announceAnswerUpdate({ questionType: 'multiple_choice', maxNumAnswerOptions: 1, value: option?.value });
    }
  }, [options, storedOption]);

  function handleChange(selectedItems: ISelectOption) {
    setErrors([]);
    setSelectedOption(selectedItems);
    announceAnswerUpdate({ questionType: 'multiple_choice', maxNumAnswerOptions: 1, value: selectedItems?.value });
  }

  const handleMobileChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const chosenOption = options.find((option) => option.value === event.target.value);
    setSelectedOption(chosenOption);
    announceAnswerUpdate({ questionType: 'multiple_choice', maxNumAnswerOptions: 1, value: chosenOption?.value });
  };

  function selectName(i: number) {
    if (!attributeName) return name;

    return `${name}[${i}][${attributeName}]`;
  }

  if (!visible) return null;

  return (
    <div className="pad-v-3">
      <label
        className="external-form__label"
        htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_text_answer`}
      >
        <span className={`external-form__label--title ${required ? 'external-form__label--required' : ''}`}>
          {title}
        </span>
        {additionalInformation && <span dangerouslySetInnerHTML={{ __html: sanitizeContent(additionalInformation) }} />}
        <div dangerouslySetInnerHTML={{ __html: multipleChoiceHelperText }} />
      </label>
      <Select
        isClearable={true}
        value={selectedOption}
        options={options}
        name={attributeName ? 'selectBox' : name}
        inputId={inputId || name}
        className={`react-select ${useMobileSelect ? 'hide-sm-block' : ''} ${
          errors.length > 0 ? 'react-select--error' : ''
        }`}
        classNamePrefix="react-select"
        isSearchable={true}
        closeMenuOnSelect={true}
        onChange={handleChange}
        placeholder={placeholder}
      />
      <select
        className="hide-at-sm-block"
        value={selectedOption?.value}
        onChange={handleMobileChange}
        id={inputId || name}
      >
        <option value="blank">Select...</option>
        {options.map((option: IReactSelectOption) => {
          return (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
      {errors.length > 0 && (
        <FormGroup>
          <FormGroupFeedback intent={Intent.DANGER}>{toSentence(initialErrors)}</FormGroupFeedback>
        </FormGroup>
      )}
      {attributeName && selectedOption && <input type="hidden" name={selectName(0)} value={selectedOption.value} />}
      <HiddenInputs
        formResourceType={formResourceType}
        index={index}
        additionalInformation={additionalInformation}
        title={title}
        questionId={questionId}
        questionType={questionType}
      />
    </div>
  );
}
